import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  extractBoardProcessDate,
  parseBoardProcessStatus,
  toBoardProcessRecord,
} from '../../../utils/boardProcess'

export interface BoardProcessSlice {
  boardKey: string
  pendingBoardKey: string | null
  status: number | null
  hasRecord: boolean | null
  record: Record<string, any> | null
  processedAt: string | null
  loading: boolean
  executing: boolean
  requestedAt: string | null
  statusReceivedAt: string | null
  executedAt: string | null
  responseCount: number
  lastError: string | null
}

const initialState: BoardProcessSlice = {
  boardKey: '',
  pendingBoardKey: null,
  status: null,
  hasRecord: null,
  record: null,
  processedAt: null,
  loading: false,
  executing: false,
  requestedAt: null,
  statusReceivedAt: null,
  executedAt: null,
  responseCount: 0,
  lastError: null,
}

export const boardProcessSlice = createSlice({
  name: 'boardProcess',
  initialState,
  reducers: {
    boardProcessStatusRequest: (state, action: PayloadAction<{ boardKey: string }>) => {
      state.loading = true
      state.lastError = null
      state.requestedAt = new Date().toISOString()
      state.pendingBoardKey = action.payload.boardKey || state.boardKey
    },
    boardProcessExecuteRequest: (state, action: PayloadAction<{ boardKey: string }>) => {
      state.executing = true
      state.lastError = null
      state.executedAt = new Date().toISOString()
      state.pendingBoardKey = action.payload.boardKey || state.boardKey
    },
    setBoardProcessStatus: (state, action: PayloadAction<any>) => {
      const record = toBoardProcessRecord(action.payload)
      const status = parseBoardProcessStatus(action.payload)
      state.boardKey = state.pendingBoardKey || state.boardKey
      state.pendingBoardKey = null
      state.loading = false
      state.status = record ? status : null
      state.hasRecord = record !== null
      state.record = record
      state.processedAt = extractBoardProcessDate(record)
      state.statusReceivedAt = new Date().toISOString()
      state.responseCount = state.responseCount + 1
      state.lastError = null
    },
    setBoardProcessExecute: (state, action: PayloadAction<any>) => {
      const record = toBoardProcessRecord(action.payload)
      const status = parseBoardProcessStatus(action.payload)
      state.boardKey = state.pendingBoardKey || state.boardKey
      state.pendingBoardKey = null
      state.executing = false
      state.status = record ? status : null
      state.hasRecord = record !== null
      state.record = record
      state.processedAt = extractBoardProcessDate(record)
      if (record) {
        state.statusReceivedAt = new Date().toISOString()
      }
      state.responseCount = state.responseCount + 1
      state.lastError = null
    },
    setBoardProcessError: (
      state,
      action: PayloadAction<{ boardKey?: string; message?: string }>,
    ) => {
      state.boardKey = action.payload.boardKey || state.boardKey
      state.pendingBoardKey = null
      state.loading = false
      state.executing = false
      state.lastError = action.payload.message || 'Request failed'
    },
    boardProcessReset: () => initialState,
  },
})

export const {
  boardProcessStatusRequest,
  boardProcessExecuteRequest,
  setBoardProcessStatus,
  setBoardProcessExecute,
  setBoardProcessError,
  boardProcessReset,
} = boardProcessSlice.actions
